import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { color } from 'd3-color';
import { interpolateRgb } from 'd3-interpolate';
import LiquidFillGauge from 'react-liquid-gauge';

const LiquidGauge = React.memo(({ fillingLevel, startColor, endColor }) => {
    const radius = 100;

    // Ensure fillingLevel is a number
    const value = useMemo(() => {
        if (typeof fillingLevel === 'number') return fillingLevel;
        const parsedValue = parseFloat(fillingLevel);
        return Number.isNaN(parsedValue) ? 0 : parsedValue;
    }, [fillingLevel]);

    const interpolateColor = useMemo(() => interpolateRgb(startColor, endColor), [startColor, endColor]);

    const fillColor = useMemo(() => interpolateColor(value / 100), [interpolateColor, value]);

    const gradientStops = useMemo(
        () => [
            {
                key: '0%',
                stopColor: color(fillColor).darker(0.5).toString(),
                stopOpacity: 1,
                offset: '0%',
            },
            {
                key: '50%',
                stopColor: fillColor,
                stopOpacity: 0.75,
                offset: '50%',
            },
            {
                key: '100%',
                stopColor: color(fillColor).brighter(0.5).toString(),
                stopOpacity: 0.5,
                offset: '100%',
            },
        ],
        [fillColor]
    );

    const textRenderer = useCallback((props) => {
        const displayValue = props.value !== null ? Math.round(props.value) : null;
        const radius = Math.min(props.height / 2, props.width / 2);
        const textPixels = (props.textSize * radius) / 2;
        const valueStyle = {
            fontSize: textPixels,
        };
        const percentStyle = {
            fontSize: textPixels * 0.6,
        };

        if (displayValue !== null) {
            return (
                <tspan>
                    <tspan className="value" style={valueStyle}>
                        {displayValue}
                    </tspan>
                    <tspan style={percentStyle}>{props.percent}</tspan>
                </tspan>
            );
        } else {
            return (
                <tspan>
                    <tspan className="value" style={valueStyle}>
                        n.s.
                    </tspan>
                </tspan>
            );
        }
    }, []);

    return (
        <div>
            <LiquidFillGauge
                style={{ margin: '0 auto' }}
                width={radius * 2}
                height={radius * 2}
                value={value}
                percent="%"
                textSize={1}
                textOffsetX={0}
                textOffsetY={0}
                textRenderer={textRenderer}
                riseAnimation
                waveAnimation
                waveFrequency={2}
                waveAmplitude={1}
                gradient
                gradientStops={gradientStops}
                circleStyle={{
                    fill: fillColor,
                }}
                waveStyle={{
                    fill: fillColor,
                }}
                textStyle={{
                    fill: color('#444').toString(),
                    fontFamily: 'Arial',
                }}
                waveTextStyle={{
                    fill: color('#fff').toString(),
                    fontFamily: 'Arial',
                }}
            />
        </div>
    );
});

LiquidGauge.propTypes = {
    fillingLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    startColor: PropTypes.string.isRequired,
    endColor: PropTypes.string.isRequired,
};

export default LiquidGauge;